import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { configClient } from '../lib/supabase/configClient';
import type { SupportedLanguages } from '../i18n';

interface LanguageSwitcherContextType {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  availableLanguages: string[];
}

const LanguageSwitcherContext = createContext<LanguageSwitcherContextType | undefined>(undefined);

export const LanguageSwitcherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(['fr', 'en']);
  const { i18n } = useTranslation();

  // Load configuration to get available languages
  const { data: config } = useQuery({
    queryKey: ['config'],
    queryFn: () => configClient.getConfig(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Update available languages when config is loaded
  useEffect(() => {
    if (config?.available_languages) {
      const languages = Array.isArray(config.available_languages)
        ? config.available_languages
        : ['fr', 'en'];

      setAvailableLanguages(languages);
    }
  }, [config, i18n.language]);

  return (
    <LanguageSwitcherContext.Provider value={{ isVisible, setIsVisible, availableLanguages }}>
      {children}
    </LanguageSwitcherContext.Provider>
  );
};

export const useLanguageSwitcher = () => {
  const context = useContext(LanguageSwitcherContext);
  if (context === undefined) {
    throw new Error('useLanguageSwitcher must be used within a LanguageSwitcherProvider');
  }
  return context;
};